import { DatabaseIcon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDocuments, useGenerations, useRefresh, useRetry } from "../../api/knowledge-queries";
import { useFormat } from "../../i18n/format";
import type { KindTabProps } from "../../kinds/tabs";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { CopyButton } from "../../ui/copy-button";
import { EmptyState } from "../../ui/empty-state";
import { EnvBadge } from "../../ui/env-badge";
import { ErrorState } from "../../ui/error-state";
import { Skeleton } from "../../ui/skeleton";
import { toast } from "../../ui/sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table";
import { problemText } from "./problems";
import { isMoving } from "./status";

const GENERATION_VARIANT = {
  building: "info",
  live: "ok",
  retired: "default",
  failed: "crit",
} as const;

/** The knowledge base's index generations: where each is served, its counts and cost (5b). */
export function GenerationsTab({ workspace, specRef, environments }: KindTabProps) {
  const { t } = useTranslation();
  const { dateTime, decimal, number } = useFormat();
  const project = specRef.project ?? "";
  const kb = specRef.name;
  const [poll, setPoll] = useState(false);
  const generations = useGenerations(workspace, project, kb, { poll });
  const documents = useDocuments(workspace, project, kb, { poll });
  const moving = isMoving(documents.data, generations.data);
  if (moving !== poll) setPoll(moving);
  const retry = useRetry(workspace, project, kb);
  const refresh = useRefresh(workspace, project, kb);
  const tierOf = (name: string) =>
    environments.find((env) => env.name === name)?.tier ?? "development";
  return (
    <div className="space-y-3" data-testid="kb-generations">
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={retry.isPending}
          onClick={() =>
            retry.mutate(undefined, {
              onSuccess: (answer) =>
                toast.success(t("knowledge.retried", { count: answer.queued })),
              onError: (error) => toast.error(problemText(t, error)),
            })
          }
        >
          {t("knowledge.generations.retryFailed")}
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={refresh.isPending}
          onClick={() =>
            refresh.mutate(undefined, {
              onSuccess: () => toast.success(t("knowledge.generations.refreshing")),
              onError: (error) => toast.error(problemText(t, error)),
            })
          }
        >
          {t("knowledge.generations.refresh")}
        </Button>
      </div>
      {generations.error ? (
        <ErrorState
          error={generations.error}
          action={
            <Button size="sm" variant="outline" onClick={() => void generations.refetch()}>
              {t("knowledge.retryLoad")}
            </Button>
          }
        />
      ) : !generations.data ? (
        <Skeleton className="h-32" aria-label={t("knowledge.loading")} />
      ) : generations.data.length === 0 ? (
        <EmptyState
          icon={DatabaseIcon}
          title={t("knowledge.generations.empty")}
          text={t("knowledge.generations.emptyText")}
        />
      ) : (
        <div className="rounded-md border border-line bg-surface">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("knowledge.generations.digest")}</TableHead>
                <TableHead>{t("knowledge.generations.status")}</TableHead>
                <TableHead>{t("knowledge.generations.environments")}</TableHead>
                <TableHead className="text-right">{t("knowledge.status.ready")}</TableHead>
                <TableHead className="text-right">{t("knowledge.status.failed")}</TableHead>
                <TableHead className="text-right">{t("knowledge.generations.waiting")}</TableHead>
                <TableHead className="text-right">{t("knowledge.sheet.cost")}</TableHead>
                <TableHead>{t("knowledge.generations.created")}</TableHead>
                <TableHead>{t("knowledge.generations.liveSince")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {generations.data.map((generation) => (
                <TableRow key={generation.id}>
                  <TableCell>
                    <span className="inline-flex items-center gap-1">
                      <code className="text-sm">
                        {generation.digest.replace("sha256:", "").slice(0, 12)}
                      </code>
                      <CopyButton
                        value={generation.digest}
                        label={t("knowledge.generations.copyDigest")}
                      />
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant={GENERATION_VARIANT[generation.status]}>
                      {t(`knowledge.generations.statuses.${generation.status}`)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="inline-flex flex-wrap gap-1">
                      {generation.environments.length
                        ? generation.environments.map((env) => (
                            <EnvBadge key={env} tier={tierOf(env)} name={env} />
                          ))
                        : "—"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {number(generation.ready)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {number(generation.failed)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {number(generation.waiting)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {generation.cost ? decimal(generation.cost) : "—"}
                  </TableCell>
                  <TableCell>{dateTime(generation.createdAt)}</TableCell>
                  <TableCell>{generation.liveAt ? dateTime(generation.liveAt) : "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
